import React, { useState, useEffect } from "react";
import Container from "@/components/marketing/Container";
import MarqueeLogo from "@/components/marketing/MarqueeLogo";
import { base44 } from "@/api/base44Client";
import { staticLogoForName } from "@/lib/guaranteedOutlets";

// Structurally identical to SaudiTrustBadges: same <section>, same marquee
// track (animate-ticker, gap-x-10 lg:gap-x-14, pr-10 lg:pr-14, motion-reduce
// fallbacks), same MarqueeLogo component, same two-copy seamless loop. The
// only differences are the label text and the item list below.
//
// Logo resolution per item:
//   1. staticLogoForName(label) — static logo from guaranteedOutlets.js
//   2. matching database record's brand_logo_url (case-insensitive name match)
//   3. text fallback rendering the outlet name so no slot is ever empty
//
// The database fetch queries active records only, NO retail_visible filter,
// so hidden logo-holder records (e.g. Financial Times, retail_visible off)
// resolve. Aliases: "MarketWatch" -> "Marketwatch" record, "Yahoo Finance"
// -> "Yahoo Finance News" record, "Entrepreneur ME" -> "Entrepreneur ME (Staff)"
// record. The public label always uses the display value.
const LOGOS = [
  { name: "Khaleej Times" },
  { name: "Gulf News", scale: 1.1 },
  { name: "Arabian Business" },
  { name: "MENA FN" },
  { name: "Gulf Times" },
  { name: "Eye of Riyadh" },
  { name: "Reuters", scale: 0.9 },
  { name: "Marketwatch", display: "MarketWatch", scale: 1.05 },
  { name: "Business Insider", scale: 1.15 },
  { name: "Yahoo Finance News", display: "Yahoo Finance", scale: 0.9 },
  { name: "Entrepreneur ME (Staff)", display: "Entrepreneur ME", scale: 1.3 },
  { name: "Financial Times" },
];

function TrustItem({ logo, record }) {
  const label = logo.display || logo.name;
  // 1. Static logo from guaranteedOutlets.js (matched by outlet name,
  //    case-insensitive, ignoring punctuation/extra whitespace).
  // 2. Fall back to the database record's brand_logo_url.
  const src = staticLogoForName(label) || (record && record.brand_logo_url) || null;
  if (!src) {
    return (
      <span
        title={label}
        role="img"
        aria-label={label}
        className="inline-flex shrink-0 items-center h-7 lg:h-8 font-heading text-sm font-bold text-[#8b8fa3] hover:text-white transition-colors duration-300"
      >
        {label}
      </span>
    );
  }
  return <MarqueeLogo src={src} name={label} scale={logo.scale} hideOnError silhouette />;
}

export default function UaeTrustBadges() {
  const names = LOGOS.map((l) => l.name);
  const [records, setRecords] = useState({});
  useEffect(() => {
    // Strip-only fetch: active records only, NO retail_visible filter, so hidden
    // logo-holder records (e.g. Financial Times, retail_visible off) resolve.
    base44.entities.Package.filter(
      { category: "guaranteed_placements", is_active: true, name: { $in: names } },
      "-domain_authority",
      30
    )
      .then((res) => {
        const map = {};
        (res || []).forEach((p) => {
          if (p && p.name) {
            map[p.name] = p;
            map[p.name.toLowerCase()] = p;
          }
        });
        setRecords(map);
      })
      .catch(() => setRecords({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [names.join("|")]);

  const lookupRecord = (logo) =>
    records[logo.name] || records[(logo.name || "").toLowerCase()];

  return (
    <section className="py-10 lg:py-12 border-y border-border bg-slate/30">
      <Container>
        <p className="font-mono text-xs uppercase tracking-widest text-steel text-center mb-8">
          Placements and distribution across UAE and Gulf media
        </p>

        {/* Logo marquee — same treatment as homepage */}
        <div className="overflow-hidden relative">
          <div className="flex w-max animate-ticker hover:[animation-play-state:paused] motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:animate-none">
            <div className="flex shrink-0 items-center gap-x-10 lg:gap-x-14 pr-10 lg:pr-14 motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:pr-0">
              {LOGOS.map((logo, i) => (
                <TrustItem key={`a-${i}`} logo={logo} record={lookupRecord(logo)} />
              ))}
            </div>
            <div className="flex shrink-0 items-center gap-x-10 lg:gap-x-14 pr-10 lg:pr-14 motion-reduce:hidden" aria-hidden="true">
              {LOGOS.map((logo, i) => (
                <TrustItem key={`b-${i}`} logo={logo} record={lookupRecord(logo)} />
              ))}
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}